// Two-level inventory taxonomy (added 2026-06-16): a fixed set of MAIN
// categories ("groups") so the inventory list stays organised at scale, each
// with a predefined list of TYPES that the crew can extend with custom values.
//
// The item's `group` field stores the main category; the existing `category`
// field is repurposed as the "type" within that group. A startup migration
// backfills `group` on legacy items from their old flat `category` using the
// same mapping mirrored in the backend (server.py _migrate_inventory_groups).

export const MAIN_CATEGORIES = [
  "Food & Drinks",
  "Spare Parts",
  "Tools",
  "Cleaning and Consumables",
  "Safety",
  "Medical",
  "Guest Supplies",
  "Crew supplies",
  "Equipment",
  "Watertoys",
  "Other",
];

// Predefined types per main category. Crew can still add custom types on the
// fly (the form's type picker allows free entry).
export const TYPES_BY_CATEGORY = {
  "Food & Drinks": [
    "Water", "Crew Water", "Soft drinks", "Beer", "Wine/Champagne", "Spirits",
    "Snacks", "Fruits", "Dry goods", "Dairy", "Frozen",
  ],
  "Spare Parts": [
    "Engine", "Electrical", "Plumbing", "Filters", "Belts & hoses",
    "Hardware", "Hydraulics",
  ],
  "Tools": ["Hand tools", "Power tools", "Measuring", "Workshop"],
  "Cleaning and Consumables": [
    "Cleaning products", "Detergents", "Paper goods", "Consumables",
    "Galley consumables",
  ],
  "Safety": [
    "Life jackets", "Fire fighting", "Flares & signals", "EPIRB / comms", "PPE",
  ],
  "Medical": ["First aid", "Medication", "Bandages & dressings", "Instruments"],
  "Guest Supplies": ["Toiletries", "Linens", "Amenities", "Stationery"],
  "Crew supplies": ["Uniforms", "Toiletries", "Linens", "Crew amenities"],
  "Equipment": [
    "Galley equipment", "Deck equipment", "Interior equipment",
    "AV / Electronics", "Engine room",
  ],
  "Watertoys": [
    "Tender", "Jet ski", "Seabob", "Paddleboard / kayak", "Diving", "Inflatables",
  ],
  "Other": ["Other"],
};

// Legacy flat-category → main-category map (lowercased keys). Mirrors the
// Python copy in server.py so frontend display and backend migration agree.
const LEGACY_GROUP_MAP = {
  "spare parts": "Spare Parts",
  "tools": "Tools",
  "cleaning products": "Cleaning and Consumables",
  "consumables": "Cleaning and Consumables",
  "safety equipment": "Safety",
  "medical": "Medical",
  "crew supplies": "Crew supplies",
  "uniforms": "Crew supplies",
  "guest supplies": "Guest Supplies",
  "tender": "Watertoys",
  "water toys": "Watertoys",
  "snack": "Food & Drinks",
  "softdrinks": "Food & Drinks",
  "champ/wine": "Food & Drinks",
  "spirits": "Food & Drinks",
  "water": "Food & Drinks",
  "beer": "Food & Drinks",
  "crew water": "Food & Drinks",
  "fruits": "Food & Drinks",
};

export function groupForLegacyCategory(category) {
  if (!category) return "Other";
  return LEGACY_GROUP_MAP[category.trim().toLowerCase()] || "Other";
}

// Resolve an item's main category: its stored `group` if present, otherwise
// derive from the legacy `category` so un-migrated items still slot in.
export function mainCategoryForItem(item) {
  if (item.group) return item.group;
  return groupForLegacyCategory(item.category);
}
